import { Autocomplete, AutocompleteCloseReason, Chip, TextField } from '@mui/material';
import { useRef } from 'react';
import { CustomAttributeOption } from 'src/graphql/entities/custom-attributes/custom-attributes.types';
import { CustomAttributeEditProps } from 'src/graphql/entities/custom-attributes/field-view-registry/types';
import {
  getAttributeOptionByKey,
  getKindLikeValueSafely,
} from 'src/graphql/entities/custom-attributes/field-view-registry/utils';

export const MultiSelectEditor = ({
  value,
  definition: { options },
  onChange,
  onSave,
  onCancel,
  disabled,
}: CustomAttributeEditProps) => {
  const selected = getKindLikeValueSafely(value, 'multi_select') ?? [];

  const initialKeysRef = useRef(selected);

  const selectedOptions = selected
    .map((key) => getAttributeOptionByKey(options, key))
    .filter((option): option is CustomAttributeOption => option !== undefined);

  const handleClose = (_event: unknown, reason: AutocompleteCloseReason) => {
    if (reason === 'escape') {
      onCancel();
    } else if (
      !sameKeys(initialKeysRef.current, getKindLikeValueSafely(value, 'multi_select') ?? [])
    ) {
      onSave();
    } else {
      onCancel();
    }
  };

  return (
    <Autocomplete<CustomAttributeOption, true>
      multiple
      value={selectedOptions}
      options={options}
      getOptionLabel={(option) => option.value}
      isOptionEqualToValue={(a, b) => a.key === b.key}
      onChange={(_event, next) =>
        onChange({ kind: 'multi_select', value: next.map((option) => option.key) })
      }
      onClose={handleClose}
      disabled={disabled}
      size="small"
      fullWidth
      disableCloseOnSelect
      autoHighlight
      openOnFocus
      renderInput={(params) => (
        <TextField {...params} size="small" autoFocus autoComplete="off" />
      )}
      renderTags={(tagValue, getTagProps) =>
        tagValue.map((option, index) => (
          <Chip
            label={option.value}
            size="small"
            sx={{ fontSize: '0.7rem', height: '1.25rem' }}
            {...getTagProps({ index })}
            key={option.key}
          />
        ))
      }
    />
  );
};

function sameKeys(a: readonly string[], b: readonly string[]) {
  return a.length === b.length && a.every((key, index) => key === b[index]);
}
